import { useState, useEffect } from 'react';
import { X, Plus, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useLocationStore } from '@/store/location';
import type { PromotionProductRule, RuleGroup, FilterType } from '@/hooks/usePromotions';

interface ProductFilterBuilderProps {
  ruleGroupPrefix: 'basic' | 'buy' | 'get';
  rules: PromotionProductRule[];
  onChange: (rules: PromotionProductRule[]) => void;
  showExclude?: boolean;
}

interface FilterOption {
  value: string;
  label: string;
}

export function ProductFilterBuilder({
  ruleGroupPrefix,
  rules,
  onChange,
  showExclude = true,
}: ProductFilterBuilderProps) {
  const { organization } = useAuth();
  const { currentLocation } = useLocationStore();
  const locationId = currentLocation?.id;

  const includeGroup: RuleGroup = `${ruleGroupPrefix}_include` as RuleGroup;
  const excludeGroup: RuleGroup = `${ruleGroupPrefix}_exclude` as RuleGroup;

  const includeRules = rules.filter(r => r.rule_group === includeGroup);
  const excludeRules = rules.filter(r => r.rule_group === excludeGroup);

  const [showExcludeSection, setShowExcludeSection] = useState(excludeRules.length > 0);

  // Options for dropdowns
  const [products, setProducts] = useState<FilterOption[]>([]);
  const [categories, setCategories] = useState<FilterOption[]>([]);
  const [suppliers, setSuppliers] = useState<FilterOption[]>([]);

  // New filter state
  const [newIncludeType, setNewIncludeType] = useState<FilterType>('product');
  const [newIncludeSearch, setNewIncludeSearch] = useState('');
  const [newExcludeType, setNewExcludeType] = useState<FilterType>('product');
  const [newExcludeSearch, setNewExcludeSearch] = useState('');

  // Product count
  const [productCount, setProductCount] = useState<number | null>(null);

  // Load options
  useEffect(() => {
    async function loadOptions() {
      if (!locationId) return;

      const [productsRes, categoriesRes, suppliersRes] = await Promise.all([
        supabase
          .from('products')
          .select('id, name')
          .eq('location_id', locationId)
          .eq('is_active', true)
          .order('name')
          .limit(500),
        supabase
          .from('categories')
          .select('id, name')
          .eq('location_id', locationId)
          .eq('is_active', true)
          .order('name'),
        supabase
          .from('suppliers')
          .select('id, name')
          .eq('organization_id', organization?.id || '')
          .eq('is_active', true)
          .order('name'),
      ]);

      setProducts((productsRes.data || []).map((p: any) => ({ value: p.id, label: p.name })));
      setCategories((categoriesRes.data || []).map((c: any) => ({ value: c.id, label: c.name })));
      setSuppliers((suppliersRes.data || []).map((s: any) => ({ value: s.id, label: s.name })));
    }

    loadOptions();
  }, [locationId, organization?.id]);

  // Count matching products when rules change
  useEffect(() => {
    async function countProducts() {
      if (!locationId) return;

      if (includeRules.length === 0) {
        // All products
        const { count } = await supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('location_id', locationId)
          .eq('is_active', true);
        setProductCount(count);
        return;
      }

      // Build filter
      const productIds = includeRules.filter(r => r.filter_type === 'product').map(r => r.filter_value);
      const categoryIds = includeRules.filter(r => r.filter_type === 'category').map(r => r.filter_value);
      const supplierIds = includeRules.filter(r => r.filter_type === 'supplier').map(r => r.filter_value);
      const skuValues = includeRules.filter(r => r.filter_type === 'sku').map(r => r.filter_value);

      const orParts: string[] = [];
      if (productIds.length > 0) orParts.push(`id.in.(${productIds.join(',')})`);
      if (categoryIds.length > 0) orParts.push(`category_id.in.(${categoryIds.join(',')})`);
      if (supplierIds.length > 0) orParts.push(`supplier_id.in.(${supplierIds.join(',')})`);
      if (skuValues.length > 0) orParts.push(`sku.in.(${skuValues.join(',')})`);

      if (orParts.length === 0) {
        setProductCount(0);
        return;
      }

      const { count } = await supabase
        .from('products')
        .select('id', { count: 'exact', head: true })
        .eq('location_id', locationId)
        .eq('is_active', true)
        .or(orParts.join(','));

      setProductCount(count);
    }

    countProducts();
  }, [includeRules, locationId]);

  const getOptionsForType = (type: FilterType): FilterOption[] => {
    switch (type) {
      case 'product': return products;
      case 'category': return categories;
      case 'supplier': return suppliers;
      case 'sku': return [];
      default: return [];
    }
  };

  const getFilterLabel = (type: FilterType, value: string): string => {
    const options = getOptionsForType(type);
    const opt = options.find(o => o.value === value);
    if (opt) return opt.label;
    // For SKU or unmatched, just show the value
    return value;
  };

  const getFilterTypeLabel = (type: FilterType): string => {
    switch (type) {
      case 'product': return 'Product';
      case 'category': return 'Category';
      case 'supplier': return 'Supplier';
      case 'sku': return 'SKU';
      default: return type;
    }
  };

  const addRule = (group: RuleGroup, filterType: FilterType, filterValue: string) => {
    // Prevent duplicates
    const exists = rules.some(
      r => r.rule_group === group && r.filter_type === filterType && r.filter_value === filterValue
    );
    if (exists) return;

    const newRule: PromotionProductRule = {
      rule_group: group,
      filter_type: filterType,
      filter_value: filterValue,
    };
    onChange([...rules, newRule]);
  };

  const removeRule = (index: number) => {
    const groupRules = [...rules];
    groupRules.splice(index, 1);
    onChange(groupRules);
  };

  const getRuleIndex = (rule: PromotionProductRule): number => {
    return rules.findIndex(
      r => r.rule_group === rule.rule_group && r.filter_type === rule.filter_type && r.filter_value === rule.filter_value
    );
  };

  const renderFilterInput = (
    type: FilterType,
    search: string,
    setSearch: (v: string) => void,
    group: RuleGroup,
  ) => {
    if (type === 'sku') {
      return (
        <div className="flex gap-2">
          <Input
            placeholder="Enter SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && search.trim()) {
                addRule(group, 'sku', search.trim());
                setSearch('');
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (search.trim()) {
                addRule(group, 'sku', search.trim());
                setSearch('');
              }
            }}
          >
            Add
          </Button>
        </div>
      );
    }

    const options = getOptionsForType(type);
    const filteredOptions = search
      ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
      : options;

    return (
      <div className="relative">
        <Input
          placeholder={`Search ${getFilterTypeLabel(type).toLowerCase()}s...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-1"
        />
        {search && filteredOptions.length > 0 && (
          <div className="absolute z-10 top-full left-0 right-0 max-h-48 overflow-y-auto bg-popover border rounded-md shadow-lg">
            {filteredOptions.slice(0, 20).map(option => (
              <button
                key={option.value}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                onClick={() => {
                  addRule(group, type, option.value);
                  setSearch('');
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Include Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>Filter products by category, supplier, product, or SKU</span>
        </div>

        {/* Existing include filters as badges */}
        {includeRules.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {includeRules.map((rule) => (
              <Badge
                key={`${rule.filter_type}-${rule.filter_value}`}
                variant="secondary"
                className="gap-1 pl-2 pr-1 py-1"
              >
                <span className="text-xs text-muted-foreground mr-1">
                  {getFilterTypeLabel(rule.filter_type)}:
                </span>
                {getFilterLabel(rule.filter_type, rule.filter_value)}
                <button
                  type="button"
                  title="Remove filter"
                  className="ml-1 rounded-full p-0.5 hover:bg-muted-foreground/20"
                  onClick={() => removeRule(getRuleIndex(rule))}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Add new include filter */}
        <div className="flex gap-2 items-start">
          <div className="flex items-center gap-1 text-sm font-medium shrink-0 pt-2">
            <Filter className="h-3.5 w-3.5" />
            Include
          </div>
          <Select
            value={newIncludeType}
            onValueChange={(v) => setNewIncludeType(v as FilterType)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="product">Product</SelectItem>
              <SelectItem value="category">Category</SelectItem>
              <SelectItem value="supplier">Supplier</SelectItem>
              <SelectItem value="sku">SKU</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1">
            {renderFilterInput(newIncludeType, newIncludeSearch, setNewIncludeSearch, includeGroup)}
          </div>
        </div>
      </div>

      {/* Exclude Section */}
      {showExclude && (
        <>
          {!showExcludeSection ? (
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm text-primary hover:underline"
              onClick={() => setShowExcludeSection(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Exclude Products
            </button>
          ) : (
            <div className="space-y-3 border-t pt-3">
              {/* Existing exclude filters as badges */}
              {excludeRules.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {excludeRules.map((rule) => (
                    <Badge
                      key={`exc-${rule.filter_type}-${rule.filter_value}`}
                      variant="destructive"
                      className="gap-1 pl-2 pr-1 py-1"
                    >
                      <span className="text-xs opacity-75 mr-1">
                        Exclude {getFilterTypeLabel(rule.filter_type)}:
                      </span>
                      {getFilterLabel(rule.filter_type, rule.filter_value)}
                      <button
                        type="button"
                        title="Remove exclusion"
                        className="ml-1 rounded-full p-0.5 hover:bg-destructive-foreground/20"
                        onClick={() => removeRule(getRuleIndex(rule))}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Add new exclude filter */}
              <div className="flex gap-2 items-start">
                <div className="flex items-center gap-1 text-sm font-medium text-destructive shrink-0 pt-2">
                  <X className="h-3.5 w-3.5" />
                  Exclude
                </div>
                <Select
                  value={newExcludeType}
                  onValueChange={(v) => setNewExcludeType(v as FilterType)}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="category">Category</SelectItem>
                    <SelectItem value="supplier">Supplier</SelectItem>
                    <SelectItem value="sku">SKU</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex-1">
                  {renderFilterInput(newExcludeType, newExcludeSearch, setNewExcludeSearch, excludeGroup)}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Product count summary */}
      {productCount !== null && (
        <div className="text-sm text-muted-foreground border-t pt-3">
          This promotion includes{' '}
          <button type="button" className="text-primary underline font-medium">
            {productCount} product{productCount !== 1 ? 's' : ''}
          </button>
          .
        </div>
      )}
    </div>
  );
}
